import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Navigate, Link } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import ClipLoader from 'react-spinners/ClipLoader';

import { useFormatMessage, useFormatDate } from 'hooks';
import Table from 'components/Table';
import { fetchUsers, deleteUser, clearUsersData } from 'state/actions/users';
import paths from 'pages/Router/paths';
import ConfirmationModal from 'components/ConfirmationModal';
import './Users.scss';
import NavSearch from '../../components/Layout/NavSearch';
import { sortDate } from '../../utils';

function Users () {
  const { usersList, isAdmin, uid, error, loading, deleted } = useSelector(
    state => ({
      usersList: state.users.data,
      isAdmin: state.auth.userData.isAdmin,
      uid: state.auth.userData.id,
      error: state.users.error,
      loading: state.users.loading,
      deleted: state.users.deleted
    }),
    shallowEqual
  );

  const [deleteModal, setDeleteModal] = useState({
    userId: null,
    isOpen: false
  });

  const dispatch = useDispatch();

  const [search, setSearch] = useState();

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchUsers());
    }

    return () => dispatch(clearUsersData());
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (deleted && !loading) {
      setDeleteModal(prevState => ({
        userId: null,
        isOpen: false
      }));
    }
  }, [deleted, loading]);

  const NavigateAdmin = !isAdmin && <Navigate to={paths.ROOT}/>;

  const onRemoveButtonClickHandler = userId => {
    setDeleteModal(prevState => ({
      userId,
      isOpen: !prevState.isOpen
    }));
  };

  const onCloseModalHandler = () => {
    setDeleteModal({ userId: null, isOpen: false });
  };

  const onDeleteUserHandler = () => {
    dispatch(deleteUser(deleteModal.userId));
  };

  const columns = [
    {
      Header: '',
      accessor: 'photoURL',
      Cell: ({ row }) => (
        <div className="image">
          <img src={row.original.photoURL} alt="" style={{ width: '40px' }} className="rounded-circle"/>
        </div>
      ),
      disableSortBy: true
    },
    {
      Header: useFormatMessage('Users.name'),
      accessor: 'name'
    },
    {
      Header: useFormatMessage('Users.email'),
      accessor: 'email'
    },
    {
      Header: useFormatMessage('Users.admin'),
      accessor: 'isAdmin',
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {row.original.isAdmin ? (
            <span className="icon">
              <i className="feather icon-check"/>
            </span>
          ) : (
            <span className="icon">
              <i className="feather icon-x"/>
            </span>
          )}
        </small>
      )
    },
    {
      Header: useFormatMessage('Users.created'),
      accessor: 'createdAt',
      sortType: sortDate,
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {useFormatDate(row.original.createdAt, {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </small>
      )
    },
    {
      Header: '',
      id: 'actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div>
          {row.original.id !== uid  && (
            <div className="button-actions is-right">
              <Link
                to={`/users/${row.original.id}`}
                className="btn btn-sm btn-primary"
              >
                <i className="feather icon-edit"/>
              </Link>

              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => onRemoveButtonClickHandler(row.original.id)}
              >
                <i className="feather icon-trash"/>
              </button>
            </div>
          )}
       </div>
      ),
      disableSortBy: true
    }
  ];

  const data = search
    ? usersList.filter(el => {
      const clonedElem = { ...el };
      delete clonedElem.id;
      delete clonedElem.isAdmin;
      delete clonedElem.photoURL;
      return Object.values(clonedElem).some(field =>
        field.toString().toLowerCase().includes(search.toLowerCase())
      );
    })
    : usersList;
  const deleteMessage = useFormatMessage('Users.delete');

  const confirmMessage = useFormatMessage('Users.confirm');

  const permDeleteMessage = useFormatMessage('Users.permDelete');

  const cancelMessage = useFormatMessage('Users.cancel');

  return (
    <div>
      {NavigateAdmin}
      {deleteModal.isOpen && (
        <ConfirmationModal
          isActive={deleteModal.isOpen}
          isLoading={loading}
          confirmButtonMessage={deleteMessage}
          title={confirmMessage}
          body={permDeleteMessage}
          cancelButtonMessage={cancelMessage}
          onConfirmation={onDeleteUserHandler}
          onCancel={onCloseModalHandler}
        />
      )}
          <Card>
            <Card.Header>
              <div className='d-flex flex-row align-items-center'>
                <Card.Title as="h5">{useFormatMessage('Users.users')}</Card.Title>
                <NavSearch handleChange={setSearch} />
                <Link to={paths.ADD_USER} className="btn btn-primary ms-auto">
                  {useFormatMessage('Users.newUser')}
                </Link>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="b-table">
                {loading ? <ClipLoader/> : <Table columns={columns} data={data}/>}
                {error && 'Show error'}
              </div>
            </Card.Body>
          </Card>
   </div>
);
};

export default Users;
